import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { BarChart3, FileText, Database, Zap, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

interface UsageItemProps {
  icon: React.ReactNode;
  label: string;
  current: number;
  limit: number | 'unlimited';
  unit?: string;
}

function UsageItem({ icon, label, current, limit, unit = '' }: UsageItemProps) {
  const isUnlimited = limit === 'unlimited';
  const percentage = isUnlimited ? 0 : Math.min((current / (limit as number)) * 100, 100);
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && percentage >= 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium">{label}</span>
        </div>
        <span className={`font-mono ${isAtLimit ? 'text-destructive' : isNearLimit ? 'text-warning' : 'text-muted-foreground'}`}>
          {current.toLocaleString()}{unit} / {isUnlimited ? '∞' : (limit as number).toLocaleString()}{unit}
        </span>
      </div>
      <Progress 
        value={isUnlimited ? 100 : percentage} 
        className={`h-2 ${isUnlimited ? '[&>div]:bg-primary/30' : isAtLimit ? '[&>div]:bg-destructive' : isNearLimit ? '[&>div]:bg-warning' : ''}`}
      />
      {isNearLimit && !isAtLimit && (
        <p className="text-xs text-warning">Approaching limit - consider upgrading</p>
      )}
      {isAtLimit && (
        <p className="text-xs text-destructive">Limit reached - upgrade to continue</p>
      )}
    </div>
  );
}

export function UsageDashboard() {
  const navigate = useNavigate();
  const { tier, limits, nextTier, nextTierPrice } = usePlanLimits();
  const { usage, loading } = useUsageTracking();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <BarChart3 className="text-primary" size={24} />
            Usage This Month
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-3">
            <BarChart3 className="text-primary" size={24} />
            Usage This Month
          </CardTitle>
          <span className="text-sm text-muted-foreground">{currentMonth}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <UsageItem
          icon={<FileText size={16} className="text-primary" />}
          label="Invoice Scans"
          current={usage.invoiceScans}
          limit={limits.invoiceScans}
        />
        
        <UsageItem
          icon={<Database size={16} className="text-primary" />}
          label="Transactions Imported"
          current={usage.transactionsImported}
          limit={limits.transactions}
        />
        
        <UsageItem
          icon={<Zap size={16} className="text-primary" />}
          label="API Calls"
          current={usage.apiCalls}
          limit={limits.apiCallsPerDay}
          unit="/day"
        />

        {/* Current Plan Info */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Current Plan</p>
              <p className="text-lg font-bold capitalize text-primary">{tier}</p>
            </div>
            {nextTier && (
              <Button onClick={() => navigate('/pricing')} size="sm">
                <TrendingUp size={16} className="mr-2" />
                Upgrade to {nextTier} ({nextTierPrice})
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
